import React from "react";

import { css } from "@emotion/react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { CSS_MQ } from "../../styles/breakpoints";
import { ContentfulComponentCMSHook, SwarovskiOptikLocale } from "@swo/shared";
import { ImageAndProductType, SwarovskiOptikLocaleFrontend, type TranslatedValue } from "@swo/storefront";
import { ImageAndProductDescription } from "./ImageAndProductDescription";
import { ImageAndProductImage } from "./ImageAndProductImage";
import { ProductHoverCard } from "./ProductHoverCard";
import { ImageAuthorLabel } from "./ImageAuthorLabel";

import cn from "classnames";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import { T } from "../T";

export const ImageAndProductComponent: ReactTFC<
  ImageAndProductType & { localeForNumberFormat: SwarovskiOptikLocaleFrontend } & ContentfulComponentCMSHook & {
      photographyAuthorLabelTranslation: TranslatedValue;
    }
> = props => {
  return !props.additionalImage ? (
    <div css={wrapperStyles} className={getComponentCMSClassname(props?.sys?.id)}>
      <ImageAndProductImage image={props.image} requestedWidth={1400} />

      <div
        css={css`
          ${CSS_MQ.atLeastLg} {
            display: grid;
            grid-template-columns: 25% 75%;

            > :only-child {
              grid-column: 2 / span 1;
            }
          }
        `}
      >
        {props.product && (
          <ProductHoverCard product={props.product} localeForNumberFormat={props.localeForNumberFormat} />
        )}

        <ImageAndProductDescription
          headline={props.headline}
          imageAuthor={props.imageAuthor}
          richText={props.richText}
          photographyAuthorLabelTranslation={<T>{props.photographyAuthorLabelTranslation}</T>}
        />
      </div>
    </div>
  ) : (
    <div
      css={wrapperStyles}
      className={cn(
        "two-columns",
        props.layoutType === "mirrored" ? "mirrored" : undefined,
        getComponentCMSClassname(props?.sys?.id),
      )}
    >
      <div
        css={css`
          ${CSS_MQ.atLeastMd} {
            margin-top: 80px;
          }

          ${CSS_MQ.atMostSm} {
            > :only-child {
              margin-bottom: 30px;
            }
          }
        `}
      >
        <ImageAndProductImage image={props.additionalImage} requestedWidth={900} />

        {props.product || props.additionalImageAuthor ? (
          <div
            className={props.additionalImageAuthor ? "with-image-author" : undefined}
            css={css`
              &.with-image-author {
                display: grid;
                grid-template-columns: var(--size) 1fr;
                grid-gap: 10px;
              }
            `}
          >
            {props.product && (
              <ProductHoverCard product={props.product} localeForNumberFormat={props.localeForNumberFormat} />
            )}

            {props.additionalImageAuthor && (
              <div css={imageAuthorWrapperStyles}>
                <ImageAuthorLabel
                  photographyAuthorLabelTranslation={<T>{props.translations.photoAuthorLabel}</T>}
                  label={props.additionalImageAuthor}
                />
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div
        css={css`
          .mirrored & {
            ${CSS_MQ.atMostSm} {
              margin-bottom: calc(2 * var(--m));
            }
          }
        `}
      >
        <ImageAndProductImage image={props.image} requestedWidth={1400} />

        {props.imageAuthor && (
          <div css={imageAuthorWrapperStyles}>
            <ImageAuthorLabel
              photographyAuthorLabelTranslation={<T>{props.translations.photoAuthorLabel}</T>}
              label={props.imageAuthor}
            />
          </div>
        )}

        <ImageAndProductDescription
          headline={props.headline}
          richText={props.richText}
          photographyAuthorLabelTranslation={<T>{props.photographyAuthorLabelTranslation}</T>}
        />
      </div>
    </div>
  );
};

const imageAuthorWrapperStyles = css`
  ${CSS_MQ.atMostSm} {
    margin-top: 10px;
    margin-bottom: 10px;
  }

  ${CSS_MQ.atLeastMd} {
    margin-top: 15px;
  }
  text-align: right;
`;

const wrapperStyles = css`
  &:not(.columns) {
    width: var(--max-narrow-content-width);
  }

  &.two-columns {
    width: var(--max-content-width-new);

    ${CSS_MQ.atLeastMd} {
      display: grid;
      grid-column-gap: 40px;

      &:not(.mirrored) {
        grid-template-columns: 11fr 17fr;
      }

      &.mirrored {
        grid-template-columns: 17fr 11fr;
      }
    }

    ${CSS_MQ.atLeastLg} {
      grid-column-gap: 80px;
    }

    ${CSS_MQ.atMostSm} {
      display: flex;
      flex-direction: column;
    }

    &.mirrored {
      > :first-of-type {
        order: 2;
      }
    }
  }

  max-width: 100%;

  margin-left: auto;
  margin-right: auto;

  --size: 80px;
  --m: 20px;
  --spacing: 8px;

  ${CSS_MQ.atLeastLg} {
    --size: 205px;
    --spacing: 20px;
  }
`;
